import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { abrirComprovanteFinanceiro } from '../../api';

const MAX_BYTES = 5 * 1024 * 1024;

const formatarTamanho = (bytes) => {
  const n = Number(bytes || 0);
  if (!n) return '';
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const nomeUsuario = (usuario) => usuario?.nome || 'Não registrado';

export default function ComprovanteBaixa({
  formData,
  onUploadChange,
  podeAnexar,
  titulo = 'Comprovante da baixa',
}) {
  const inputRef = useRef(null);
  const [erro, setErro] = useState('');

  const selecionar = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setErro('Selecione um arquivo PDF.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setErro('O comprovante deve ter no máximo 5 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setErro('');
      onUploadChange?.({
        nome: file.name,
        mimeType: 'application/pdf',
        tamanho: file.size,
        base64: reader.result,
      });
    };
    reader.readAsDataURL(file);
  };

  const atual = formData?.comprovanteBaixa;
  const upload = formData?.comprovanteBaixaUpload;
  const historicos = Array.isArray(formData?.comprovantesHistoricos)
    ? formData.comprovantesHistoricos
    : [];

  const abrir = async (item, baixar = false) => {
    if (!item?._id) return;
    const result = await abrirComprovanteFinanceiro(item._id, item.nome, baixar);
    if (result?.success === false) {
      window.alert(result.message || 'Não foi possível abrir o comprovante.');
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        {titulo}
      </Typography>

      {erro && <Alert severity="warning" sx={{ mb: 1 }}>{erro}</Alert>}

      <Stack spacing={1}>
        <Typography variant="body2">
          <strong>Lançado por:</strong> {nomeUsuario(formData?.lancadoPor || formData?.user)}
        </Typography>
        <Typography variant="body2">
          <strong>Baixa realizada por:</strong> {nomeUsuario(formData?.baixadoPor)}
        </Typography>

        {upload && (
          <Alert severity="info">
            PDF selecionado: <strong>{upload.nome}</strong>
            {upload.tamanho ? ` (${formatarTamanho(upload.tamanho)})` : ''}
          </Alert>
        )}

        {atual?._id && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip label={atual.nome || 'Comprovante PDF'} size="small" />
            <Button size="small" onClick={() => abrir(atual, false)}>Visualizar</Button>
            <Button size="small" onClick={() => abrir(atual, true)}>Baixar</Button>
          </Stack>
        )}

        {podeAnexar && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,.pdf"
              style={{ display: 'none' }}
              onChange={selecionar}
            />
            <Box>
              <Button variant="outlined" onClick={() => inputRef.current?.click()}>
                {upload || atual ? 'Selecionar outro PDF' : 'Selecionar PDF'}
              </Button>
              <Typography variant="caption" sx={{ ml: 1 }}>
                PDF de até 5 MB
              </Typography>
            </Box>
          </>
        )}

        {historicos.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Comprovantes preservados de baixas anteriores
            </Typography>
            {historicos.map((item, index) => (
              <Stack key={`${item?._id || index}`} direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">
                  {item?.nome || `Comprovante ${index + 1}`}
                </Typography>
                {item?._id && (
                  <>
                    <Button size="small" onClick={() => abrir(item, false)}>Visualizar</Button>
                    <Button size="small" onClick={() => abrir(item, true)}>Baixar</Button>
                  </>
                )}
              </Stack>
            ))}
          </>
        )}
      </Stack>
    </Paper>
  );
}
